import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { createManualChampionship, getSeriesList } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const inp = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    background: '#161b22', border: '1px solid #30363d', borderRadius: 6,
    color: '#e6edf3', fontSize: '0.9rem', padding: '10px 14px',
    outline: 'none', marginTop: 6, marginBottom: 16,
};
const lbl = { display: 'block', fontSize: '0.82rem', color: '#8b949e', fontWeight: 500 };
const sectionTitle = {
    fontSize: '0.75rem', fontWeight: 700, color: '#8b949e',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid #21262d', paddingBottom: 8, marginBottom: 20, marginTop: 28,
};

const TRACK_CATEGORIES = ['Road', 'Oval', 'Dirt Road', 'Dirt Oval'];
const QUARTERS = [1, 2, 3, 4];

function CreateChampionship() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const password = sessionStorage.getItem(`organizer_pwd_${id}`) || '';

    const [seriesList, setSeriesList] = useState([]);
    const [name, setName] = useState('');
    const [seriesName, setSeriesName] = useState('');
    const [trackCategory, setTrackCategory] = useState('');
    const [seasonYear, setSeasonYear] = useState(new Date().getFullYear());
    const [seasonQuarter, setSeasonQuarter] = useState('');
    const [active, setActive] = useState('true');
    const [totalRaces, setTotalRaces] = useState('');
    const [numDrops, setNumDrops] = useState(0);
    const [regulationText, setRegulationText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        getSeriesList().then(setSeriesList).catch(() => {});
    }, []);

    if (!password) {
        navigate(`/leagues/${id}/organizer`);
        return null;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        if (!name.trim()) { setError(t('organizer.champNameRequired')); return; }

        setLoading(true);
        try {
            const result = await createManualChampionship(id, {
                password,
                name: name.trim(),
                series_name: seriesName.trim(),
                track_category: trackCategory || null,
                season_year: seasonYear || null,
                season_quarter: seasonQuarter || null,
                active,
                total_races: totalRaces || null,
                num_drops: numDrops,
                regulation_text: regulationText,
            });
            navigate(`/leagues/${id}/organizer/championships/${result.championship_id}/points`);
        } catch (err) {
            setError(err?.error || t('organizer.errorSave'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <main style={{ flex: 1, maxWidth: 700, margin: '0 auto', padding: '48px 24px', width: '100%' }}>

                <div style={{ fontSize: '0.8rem', color: '#8b949e', marginBottom: 8 }}>
                    <a href={`/leagues/${id}/organizer`} style={{ color: '#58a6ff', textDecoration: 'none' }}>
                        ← {t('organizer.backToPanel')}
                    </a>
                </div>

                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', marginBottom: 32 }}>
                    {t('organizer.addChampionship')}
                </h1>

                {error && (
                    <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: '0.85rem' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>

                    <div style={sectionTitle}>{t('organizer.champBasicInfo')}</div>

                    <label style={lbl}>{t('organizer.champName')} *</label>
                    <input
                        style={inp}
                        type="text"
                        placeholder={t('organizer.champNamePlaceholder')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        autoFocus
                    />

                    <label style={lbl}>{t('organizer.champSeriesName')}</label>
                    <select style={{ ...inp }} value={seriesName} onChange={e => setSeriesName(e.target.value)}>
                        <option value="">— {t('organizer.seriesPlaceholder')} —</option>
                        {seriesList.map(s => (
                            <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                    </select>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
                        <div>
                            <label style={lbl}>{t('organizer.champTrackCategory')}</label>
                            <select style={{ ...inp }} value={trackCategory} onChange={e => setTrackCategory(e.target.value)}>
                                <option value="">—</option>
                                {TRACK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>{t('organizer.champSeasonYear')}</label>
                            <input
                                style={inp}
                                type="number"
                                min="2000" max="2100"
                                value={seasonYear}
                                onChange={e => setSeasonYear(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={lbl}>{t('organizer.champSeasonQuarter')}</label>
                            <select style={{ ...inp }} value={seasonQuarter} onChange={e => setSeasonQuarter(e.target.value)}>
                                <option value="">—</option>
                                {QUARTERS.map(q => <option key={q} value={q}>S{q}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>{t('organizer.champStatus')}</label>
                            <select style={{ ...inp }} value={active} onChange={e => setActive(e.target.value)}>
                                <option value="true">{t('organizer.champStatusActive')}</option>
                                <option value="false">{t('organizer.champStatusFinished')}</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                            <label style={lbl}>{t('organizer.champTotalRaces')}</label>
                            <input
                                style={inp}
                                type="number"
                                min="1" max="100"
                                placeholder="—"
                                value={totalRaces}
                                onChange={e => setTotalRaces(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={lbl}>{t('organizer.champNumDrops')}</label>
                            <input
                                style={inp}
                                type="number"
                                min="0" max="50"
                                value={numDrops}
                                onChange={e => setNumDrops(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={sectionTitle}>{t('organizer.champRegulation')}</div>

                    <label style={lbl}>{t('organizer.champRegulationText')}</label>
                    <textarea
                        style={{ ...inp, resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
                        placeholder={t('organizer.champRegulationPlaceholder')}
                        value={regulationText}
                        onChange={e => setRegulationText(e.target.value)}
                    />

                    <div style={{ background: '#1b2a3a', border: '1px solid #1f4068', borderRadius: 6, padding: '12px 16px', marginBottom: 24, fontSize: '0.82rem', color: '#8b949e' }}>
                        ℹ️ {t('organizer.champCreateHint')}
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            display: 'block', width: '100%',
                            background: '#e8c84a', color: '#0d1117', border: 'none',
                            borderRadius: 6, padding: '12px 0', fontWeight: 700,
                            fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                        }}
                    >
                        {loading ? t('common.loading') : t('organizer.champCreateBtn')}
                    </button>

                </form>
            </main>
            <Footer />
        </div>
    );
}

export default CreateChampionship;
